import React from 'react'
import { useIntl } from 'react-intl'

import * as DialogPrimitive from '@radix-ui/react-dialog'

import { Button, type ButtonProps } from '@/components/atoms/Button/Button.tsx'
import { Dialog, type DialogProps, DialogTrigger } from '@/components/atoms/Dialog/Dialog.tsx'
import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { TextArea } from '@/components/atoms/TextArea/TextArea.tsx'
import { VoiceMessageButton, VoiceRecorderPanel } from '@/components/molecules/VoiceRecorderPanel'
import { useVoiceRecorder, type VoiceRecording } from '@/hooks/useVoiceRecorder'
import { cn } from '@/lib/utils.ts'

export interface BroadcastMessageDialogProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  variant?: ButtonProps['variant']
  onOpenChange?: DialogProps['onOpenChange']
  onSend: (message: string) => Promise<void>
  onSendVoice?: (blob: Blob, durationMs: number) => Promise<void>
  children?: React.ReactNode
}

export const BroadcastMessageDialog = React.forwardRef<HTMLDivElement, BroadcastMessageDialogProps>(
  ({ className, variant, onOpenChange, onSend, onSendVoice, children, ...props }, ref) => {
    const intl = useIntl()
    const [open, setOpen] = React.useState(false)
    const [message, setMessage] = React.useState('')
    const [sending, setSending] = React.useState(false)
    const recorder = useVoiceRecorder()
    const openingRef = React.useRef(0)
    const voiceActive = !!onSendVoice && recorder.state.status !== 'idle'

    const handleSendVoice = async ({ blob, durationMs }: VoiceRecording) => {
      const opening = openingRef.current
      await onSendVoice?.(blob, durationMs)
      // A clip sent before the dialog was closed still goes out; its late end leaves a reopened dialog alone.
      if (opening !== openingRef.current) return
      setMessage('')
      setOpen(false)
    }

    const handleSend = async () => {
      if (!message.trim()) return

      setSending(true)
      try {
        await onSend(message)
        setMessage('')
        setOpen(false)
      } finally {
        setSending(false)
      }
    }

    return (
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
          onOpenChange?.(value)
          if (!value) {
            openingRef.current++
            setMessage('')
            recorder.discard()
          }
        }}>
        <DialogTrigger
          asChild
          onClick={(event) => {
            event.stopPropagation()
          }}>
          {children || (
            <Button className="py-1.5" variant={variant}>
              {intl.formatMessage({ id: 'message.broadcast.trigger' })}
            </Button>
          )}
        </DialogTrigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
          <DialogPrimitive.Content
            className={cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 flex w-full max-w-[1004px] translate-x-[-50%] translate-y-[-50%] flex-col gap-6 rounded-[20px] bg-white px-6 pt-6 pb-12 shadow-lg duration-200',
              className
            )}
            ref={ref}
            {...props}>
            <DialogPrimitive.Close className="rounded-allocated absolute top-6 right-6 opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
              <Icons.close className="h-4 w-4 fill-black" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>

            <div className="flex gap-6 py-10">
              <div className="flex w-9 flex-col items-center gap-2 pt-1.5">
                <p className="leading-[1.2] text-gray-900">{intl.formatTime(new Date())}</p>
                <Icons.megaphone className="h-8 w-[30px] text-black" />
              </div>

              <div className="flex flex-1 flex-col gap-2">
                <h2 className="text-[25px] leading-[1.2] font-normal text-gray-800">
                  {intl.formatMessage({ id: 'message.broadcast.label' })}
                </h2>
                {voiceActive ? (
                  <VoiceRecorderPanel
                    recorder={recorder}
                    onSend={handleSendVoice}
                    size="sm"
                    className="h-[198px] px-6"
                  />
                ) : (
                  <TextArea
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value)
                    }}
                    placeholder={intl.formatMessage({ id: 'message.broadcast.placeholder' })}
                    className="h-[198px] w-full resize-none"
                  />
                )}
              </div>
            </div>

            <div
              className={cn('flex flex-wrap items-center justify-start gap-2 py-6 pl-[62px]', voiceActive && 'hidden')}>
              <Button
                disabled={!message.trim() || sending}
                loading={sending}
                onClick={async (event) => {
                  event.stopPropagation()
                  await handleSend()
                }}
                className="bg-blue-600 px-6 py-1.5 text-sm leading-[1.2] font-bold text-white hover:bg-blue-700">
                {intl.formatMessage({ id: 'message.broadcast.confirm' })}
              </Button>
              {onSendVoice && <VoiceMessageButton recorder={recorder} disabled={sending} size="sm" />}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </Dialog>
    )
  }
)
